import { logger } from '@/utils/logger';
import type {
  Connection,
  Edge,
  Node,
  NodeTypes,
  OnConnectStartParams,
} from '@xyflow/react';
import {
  ReactFlowProvider,
  useOnSelectionChange,
  useReactFlow,
} from '@xyflow/react';
import App from 'antd/es/app';
import Dropdown from 'antd/es/dropdown';
import React, { useCallback, useState } from 'react';

import type { PipelineConfiguration } from '@mai/types';

import { useNodeTypes } from '../../hooks/useNodeTypes';
import { usePipelineFlow } from '../../hooks/usePipelineFlow';
import {
  addBlockToPipelineConfig,
  createBlockFromDefinition,
} from '../../utils/block-creation';
import { parseHandleId } from '../../utils/handle-ids';
import { calculateAutoLayout } from '../../utils/react-flow-adapters';
import type { BlockDefinition } from '../BlockPalette';
import {
  createEdgeContextMenuItems,
  createNodeContextMenuItems,
} from '../ContextMenu';
import { CanvasContainer, FlowCanvas, ReadOnlyBadge } from '../ui';

interface WorkflowCanvasProps {
  mode?: 'yaml' | 'gui';
  pipelineConfig: PipelineConfiguration | null;
  onPipelineConfigChange: (config: PipelineConfiguration) => void;
  onNodeClick?: (nodeId: string) => void;
  selectedNodeId?: string | null;
  onNodeDelete?: (nodeId: string) => void;
  onEdgeClick?: (edgeId: string) => void;
  selectedEdgeId?: string | null;
  // Position tracking for drag-and-drop
  nodePositions?: Map<string, { x: number; y: number }>;
  onNodePositionsChange?: (
    positions: Map<string, { x: number; y: number }>,
  ) => void;
}

interface WorkflowCanvasInnerProps extends WorkflowCanvasProps {
  nodeTypes: NodeTypes;
}

// Inner component that uses React Flow context for drag and drop
const WorkflowCanvasInner: React.FC<WorkflowCanvasInnerProps> = ({
  mode = 'yaml',
  pipelineConfig,
  onPipelineConfigChange,
  onNodeClick,
  selectedNodeId,
  onNodeDelete,
  onEdgeClick,
  selectedEdgeId,
  nodeTypes,
  nodePositions = new Map(),
  onNodePositionsChange,
}) => {
  const { message } = App.useApp();
  const reactFlowInstance = useReactFlow();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    type: 'node' | 'edge' | null;
    nodeId: string | null;
    edgeId: string | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    type: null,
    nodeId: null,
    edgeId: null,
  });

  // Connection state for visual indicators
  const [activeConnection, setActiveConnection] = useState<{
    sourceNodeId: string;
    sourceHandle: string;
  } | null>(null);

  // Handle node context menu
  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, nodeId: string) => {
      event.preventDefault();
      logger.info('WorkflowCanvas node context menu handler called', {
        nodeId,
        x: event.clientX,
        y: event.clientY,
      });
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        type: 'node',
        nodeId,
        edgeId: null,
      });
    },
    [],
  );

  // Handle edge context menu
  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      logger.info('WorkflowCanvas edge context menu handler called', {
        edgeId: edge.id,
        x: event.clientX,
        y: event.clientY,
      });
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        type: 'edge',
        nodeId: null,
        edgeId: edge.id,
      });
    },
    [],
  );

  // Handle connection start - track source for visual indicators
  const handleConnectStart = useCallback(
    (_event: MouseEvent | TouchEvent, params: OnConnectStartParams) => {
      if (mode === 'gui' && params.nodeId) {
        logger.info('Connection started from:', {
          nodeId: params.nodeId,
          handleId: params.handleId,
        });
        setActiveConnection({
          sourceNodeId: params.nodeId,
          sourceHandle: params.handleId || 'default',
        });
      }
    },
    [mode],
  );

  // Handle connection end - clear visual indicators
  const handleConnectEnd = useCallback((_event: MouseEvent | TouchEvent) => {
    logger.info('Connection ended');
    setActiveConnection(null);
  }, []);

  // Helper function to determine if a target handle is valid for the current connection
  const isValidTargetHandle = useCallback(
    (targetNodeId: string, targetHandle: string): boolean => {
      if (!activeConnection || !pipelineConfig) {
        return false;
      }

      // Parse prefixed handle ID to get port name
      const targetPort = parseHandleId(targetHandle).portName;

      // Check if target port already has an inbound connection
      const existingInboundConnection = pipelineConfig.connections.find(
        (conn) =>
          conn.target_block_id === targetNodeId &&
          conn.target_port === targetPort,
      );

      return !existingInboundConnection;
    },
    [activeConnection, pipelineConfig],
  );

  // Handle pipeline flow state and conversions
  const { nodes, edges, error, handleNodesChange, handleEdgesChange } =
    usePipelineFlow({
      mode,
      pipelineConfig,
      onPipelineConfigChange,
      nodePositions,
      onNodePositionsChange,
    });

  // Update nodes with selection state, context menu handler, and connection validation
  const nodesWithSelection = React.useMemo(() => {
    return nodes.map((node) => {
      // Find edges connected to this node's target handles
      const connectedTargetHandles = edges
        .filter((edge) => edge.target === node.id)
        .map((edge) => edge.targetHandle || '')
        .filter(Boolean);

      return {
        ...node,
        selected: node.id === selectedNodeId,
        data: {
          ...node.data,
          onContextMenu: handleNodeContextMenu,
          // Pass connection state for visual indicators
          activeConnection,
          isValidTargetHandle: (targetHandle: string) =>
            isValidTargetHandle(node.id, targetHandle),
          // Pass connected target handles for dynamic port management
          connectedTargetHandles,
          // Pass mode information for readonly behavior
          mode,
        },
      };
    });
  }, [
    nodes,
    edges,
    selectedNodeId,
    handleNodeContextMenu,
    activeConnection,
    isValidTargetHandle,
    mode,
  ]);

  // Update edges with selection state
  const edgesWithSelection = React.useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      selected: edge.id === selectedEdgeId,
      style: {
        ...edge.style,
        strokeWidth: edge.id === selectedEdgeId ? 4 : 3,
        stroke: edge.id === selectedEdgeId ? '#1890ff' : '#64748b',
      },
    }));
  }, [edges, selectedEdgeId]);

  const isReadOnly = mode === 'yaml';

  // Handle selection changes from React Flow
  useOnSelectionChange({
    onChange: ({ nodes: selectedNodes }) => {
      if (mode === 'gui' && onNodeClick) {
        const firstSelectedNode = selectedNodes[0];
        if (firstSelectedNode) {
          onNodeClick(firstSelectedNode.id);
        } else {
          // Clear selection when no nodes are selected
          onNodeClick('');
        }
      }
    },
  });

  // Click handlers for GUI mode
  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (mode === 'gui' && onNodeClick) {
      onNodeClick(node.id);
    }
  };

  const handleEdgeClick = (_event: React.MouseEvent, edge: Edge) => {
    // Hide context menu on normal clicks
    setContextMenu((prev) => ({ ...prev, visible: false }));

    // Clear node selection when selecting an edge
    if (mode === 'gui' && onNodeClick) {
      onNodeClick('');
    }

    // Select the edge
    if (mode === 'gui' && onEdgeClick) {
      onEdgeClick(edge.id);
    }
  };

  // Handle drag over to allow drop
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle drop from palette
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!pipelineConfig || mode !== 'gui') {
        return;
      }

      try {
        const dragData = JSON.parse(
          event.dataTransfer.getData('application/json'),
        );

        if (dragData.type !== 'block' || !dragData.blockDefinition) {
          return;
        }

        const blockDefinition = dragData.blockDefinition as BlockDefinition;

        // Get the drop position directly using React Flow's coordinate system
        const dropPosition = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        // Center the block on the drop position (nodes are 200x80px)
        const position = {
          x: dropPosition.x - 100, // Half of 200px width
          y: dropPosition.y - 40, // Half of 80px height
        };

        logger.info('Drop positioning:', {
          clientX: event.clientX,
          clientY: event.clientY,
          dropPosition,
          centeredPosition: position,
        });

        // Create new block
        const newBlock = createBlockFromDefinition(blockDefinition, {
          position,
        });
        logger.info('Created new block:', newBlock);

        // Add block to pipeline configuration
        const updatedConfig = addBlockToPipelineConfig(
          pipelineConfig,
          newBlock,
          position,
        );
        logger.info('Updated pipeline config:', {
          oldBlockCount: Object.keys(pipelineConfig.blocks).length,
          newBlockCount: Object.keys(updatedConfig.blocks).length,
          newBlockId: newBlock.id,
        });

        // Store position in position map
        if (onNodePositionsChange) {
          const updatedPositions = new Map(nodePositions);
          updatedPositions.set(newBlock.id, position);
          onNodePositionsChange(updatedPositions);
          logger.info('Updated node positions:', updatedPositions);
        }

        // Update the pipeline configuration
        onPipelineConfigChange(updatedConfig);
      } catch (error) {
        logger.warn('Error handling block drop:', error);
      }
    },
    [
      pipelineConfig,
      mode,
      reactFlowInstance,
      onPipelineConfigChange,
      nodePositions,
      onNodePositionsChange,
    ],
  );

  // Delete node function
  const deleteNode = useCallback(
    (nodeId: string) => {
      if (mode !== 'gui' || !pipelineConfig) {
        return;
      }

      try {
        // Create a new pipeline config without the deleted node
        const updatedBlocks = { ...pipelineConfig.blocks };
        delete updatedBlocks[nodeId];

        // Remove any connections involving the deleted node
        const updatedConnections = pipelineConfig.connections.filter(
          (connection) =>
            connection.source_block_id !== nodeId &&
            connection.target_block_id !== nodeId,
        );

        const updatedConfig = {
          ...pipelineConfig,
          blocks: updatedBlocks,
          connections: updatedConnections,
        };

        // Remove position from position map
        if (onNodePositionsChange) {
          const updatedPositions = new Map(nodePositions);
          updatedPositions.delete(nodeId);
          onNodePositionsChange(updatedPositions);
        }

        // Update the pipeline configuration directly
        onPipelineConfigChange(updatedConfig);

        // Call external callback if provided
        if (onNodeDelete) {
          onNodeDelete(nodeId);
        }

        logger.info('Node deleted:', nodeId);
      } catch (error) {
        logger.warn('Error deleting node:', error);
      }
    },
    [
      mode,
      pipelineConfig,
      onPipelineConfigChange,
      onNodeDelete,
      nodePositions,
      onNodePositionsChange,
    ],
  );

  // Handle connection creation
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (
        mode !== 'gui' ||
        !pipelineConfig ||
        !connection.source ||
        !connection.target
      ) {
        return;
      }

      try {
        logger.info('Creating connection:', connection);

        // Create new pipeline connection - parse prefixed handle IDs
        const sourcePort = connection.sourceHandle
          ? parseHandleId(connection.sourceHandle).portName
          : 'default';
        const targetPort = connection.targetHandle
          ? parseHandleId(connection.targetHandle).portName
          : 'default';

        const newConnection = {
          source_block_id: connection.source,
          target_block_id: connection.target,
          source_port: sourcePort,
          target_port: targetPort,
        };

        // Validate: Check if target port already has an inbound connection
        const existingInboundConnection = pipelineConfig.connections.find(
          (conn) =>
            conn.target_block_id === newConnection.target_block_id &&
            conn.target_port === newConnection.target_port,
        );

        if (existingInboundConnection) {
          // Get target block for better error message
          const targetBlock =
            pipelineConfig.blocks[newConnection.target_block_id];
          const targetBlockLabel = targetBlock
            ? `"${targetBlock.type}" block`
            : `block "${newConnection.target_block_id}"`;

          void message.error(
            `Connection rejected: Port "${newConnection.target_port}" on ${targetBlockLabel} already has an inbound connection. Each input port can only have one connection.`,
          );

          logger.warn('Connection rejected - target port already connected:', {
            newConnection,
            existingConnection: existingInboundConnection,
          });
          return;
        }

        // Capture current node positions before updating config to prevent jumping
        if (onNodePositionsChange) {
          const currentPositions = new Map(nodePositions);
          nodes.forEach((node) => {
            currentPositions.set(node.id, {
              x: node.position.x,
              y: node.position.y,
            });
          });
          onNodePositionsChange(currentPositions);
        }

        // Add connection to pipeline config
        const updatedConfig = {
          ...pipelineConfig,
          connections: [...pipelineConfig.connections, newConnection],
        };

        onPipelineConfigChange(updatedConfig);
        logger.info('Connection created:', newConnection);

        // Show success feedback
        void message.success('Connection created successfully');
      } catch (error) {
        logger.warn('Error creating connection:', error);
      }
    },
    [
      mode,
      pipelineConfig,
      onPipelineConfigChange,
      nodes,
      nodePositions,
      onNodePositionsChange,
    ],
  );

  // Validate connection attempts before they happen
  const isValidConnection = useCallback(
    (connection: Connection | Edge): boolean => {
      // Handle both Connection and Edge types
      const source = 'source' in connection ? connection.source : null;
      const target = 'target' in connection ? connection.target : null;
      const targetHandle =
        'targetHandle' in connection ? connection.targetHandle : null;

      if (!pipelineConfig || !source || !target) {
        return false;
      }

      // Check if target port already has an inbound connection
      const targetPort = targetHandle || 'default';
      const existingInboundConnection = pipelineConfig.connections.find(
        (conn) =>
          conn.target_block_id === target && conn.target_port === targetPort,
      );

      if (existingInboundConnection) {
        // Silently reject invalid connections - visual indicators will show valid targets
        return false;
      }

      return true;
    },
    [pipelineConfig],
  );

  // Delete edge function
  const deleteEdge = useCallback(
    (edgeId: string) => {
      if (mode !== 'gui' || !pipelineConfig) {
        return;
      }

      try {
        // Find the edge in the current React Flow edges to get the complete connection info
        const edge = edges.find((e) => e.id === edgeId);
        if (!edge) {
          logger.warn('Edge not found in React Flow state:', edgeId);
          void message.error('Connection not found');
          return;
        }

        // Parse prefixed handle IDs to get actual port names
        const sourcePort = edge.sourceHandle
          ? parseHandleId(edge.sourceHandle).portName
          : 'default';
        const targetPort = edge.targetHandle
          ? parseHandleId(edge.targetHandle).portName
          : 'default';

        // Find the exact connection to remove using all connection properties
        const connectionToDelete = pipelineConfig.connections.find(
          (conn) =>
            conn.source_block_id === edge.source &&
            conn.target_block_id === edge.target &&
            conn.source_port === sourcePort &&
            conn.target_port === targetPort,
        );

        if (!connectionToDelete) {
          logger.warn('Connection not found in pipeline config for edge:', {
            edgeId,
            edge,
            connections: pipelineConfig.connections,
          });
          void message.error('Connection not found in pipeline configuration');
          return;
        }

        // Capture current node positions before updating config to prevent jumping
        if (onNodePositionsChange) {
          const currentPositions = new Map(nodePositions);
          nodes.forEach((node) => {
            currentPositions.set(node.id, {
              x: node.position.x,
              y: node.position.y,
            });
          });
          onNodePositionsChange(currentPositions);
        }

        // Remove the connection from pipeline config
        const updatedConnections = pipelineConfig.connections.filter(
          (conn) =>
            !(
              conn.source_block_id === connectionToDelete.source_block_id &&
              conn.target_block_id === connectionToDelete.target_block_id &&
              conn.source_port === connectionToDelete.source_port &&
              conn.target_port === connectionToDelete.target_port
            ),
        );

        const updatedConfig = {
          ...pipelineConfig,
          connections: updatedConnections,
        };

        onPipelineConfigChange(updatedConfig);
        void message.success('Connection deleted successfully');
        logger.info('Connection deleted:', connectionToDelete);
      } catch (error) {
        logger.warn('Error deleting connection:', error);
        void message.error('Failed to delete connection');
      }
    },
    [mode, pipelineConfig, onPipelineConfigChange, edges],
  );

  // Handle context menu actions
  const handleContextMenuAction = useCallback(
    (action: string) => {
      logger.info('Context menu action:', {
        action,
        type: contextMenu.type,
        nodeId: contextMenu.nodeId,
        edgeId: contextMenu.edgeId,
      });

      if (contextMenu.type === 'node' && contextMenu.nodeId) {
        switch (action) {
          case 'delete':
            logger.info('Deleting node:', contextMenu.nodeId);
            deleteNode(contextMenu.nodeId);
            break;
          case 'configure':
            if (onNodeClick) {
              onNodeClick(contextMenu.nodeId);
            }
            break;
          case 'copy':
            // TODO: Implement copy functionality
            logger.info('Copy node:', contextMenu.nodeId);
            break;
        }
      } else if (contextMenu.type === 'edge' && contextMenu.edgeId) {
        switch (action) {
          case 'delete':
            logger.info('Deleting edge:', contextMenu.edgeId);
            deleteEdge(contextMenu.edgeId);
            break;
        }
      }

      // Hide context menu after action
      setContextMenu((prev) => ({ ...prev, visible: false }));
    },
    [
      contextMenu.type,
      contextMenu.nodeId,
      contextMenu.edgeId,
      deleteNode,
      deleteEdge,
      onNodeClick,
    ],
  );

  // Hide context menu when clicking elsewhere
  const handlePaneClick = (_event: React.MouseEvent) => {
    if (mode === 'gui') {
      // Clear node selection when clicking on empty canvas
      if (onNodeClick) {
        onNodeClick('');
      }
      // Clear edge selection when clicking on empty canvas
      if (onEdgeClick) {
        onEdgeClick('');
      }
    }
    // Hide context menu
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  // Handle auto layout
  const handleAutoLayout = useCallback(() => {
    if (mode !== 'gui' || !pipelineConfig) {
      return;
    }

    try {
      // Calculate new positions using the auto layout algorithm
      const blocks = Object.values(pipelineConfig.blocks);
      const connections = pipelineConfig.connections;

      const newPositions = calculateAutoLayout(blocks, connections, true); // Use Dagre layout

      if (onNodePositionsChange) {
        onNodePositionsChange(newPositions);
        void message.success('Auto layout applied successfully');
        logger.info('Auto layout applied:', {
          nodeCount: blocks.length,
          connectionCount: connections.length,
        });
      }
    } catch (error) {
      logger.warn('Error applying auto layout:', error);
      void message.error('Failed to apply auto layout');
    }
  }, [mode, pipelineConfig, onNodePositionsChange]);

  // Show error state if conversion failed
  if (error) {
    return (
      <CanvasContainer isReadOnly={isReadOnly}>
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#ff4d4f',
          }}
        >
          Error: {error}
        </div>
      </CanvasContainer>
    );
  }

  return (
    <CanvasContainer isReadOnly={isReadOnly}>
      {isReadOnly && <ReadOnlyBadge />}
      <FlowCanvas
        nodes={nodesWithSelection}
        edges={edgesWithSelection}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onEdgeContextMenu={handleEdgeContextMenu}
        onPaneClick={handlePaneClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        isValidConnection={isValidConnection}
        interactive={!isReadOnly}
        onAutoLayout={handleAutoLayout}
      />

      {/* Context Menu */}
      {contextMenu.visible && (contextMenu.nodeId || contextMenu.edgeId) && (
        <Dropdown
          open={true}
          menu={{
            items: (contextMenu.type === 'node' && contextMenu.nodeId
              ? createNodeContextMenuItems(
                  contextMenu.nodeId,
                  contextMenu.nodeId === selectedNodeId,
                )
              : contextMenu.type === 'edge' && contextMenu.edgeId
                ? createEdgeContextMenuItems(contextMenu.edgeId)
                : []
            ).map((item) => ({
              key: item.key,
              label: item.label,
              icon: item.icon,
              danger: item.danger,
              onClick: () => handleContextMenuAction(item.key),
            })),
          }}
          getPopupContainer={() => document.body}
          overlayStyle={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              width: 0,
              height: 0,
            }}
          />
        </Dropdown>
      )}
    </CanvasContainer>
  );
};

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = React.memo(
  (props) => {
    // Get node types for React Flow
    const nodeTypes = useNodeTypes();

    return (
      <ReactFlowProvider>
        <WorkflowCanvasInner {...props} nodeTypes={nodeTypes} />
      </ReactFlowProvider>
    );
  },
);
